// Bandeau « Ajout rapide » (accueil) : ajoute pesée / repas / eau / sport depuis
// des popups, sans naviguer. Réutilise les hooks de mutation, useSubmit, frequentFoods.
import { useState } from 'react';
import {
  type Activity,
  frequentFoods,
  MEAL_LABELS,
  MEAL_ORDER,
  type Meal,
  SPORT_LABELS,
  SPORT_ORDER,
  todayKey,
} from '@/domain';
import {
  useConsumptions,
  useCreateConsumption,
  useCreateExercise,
  useCreateWater,
  useCreateWeight,
} from '@/hooks/queries';
import { useSubmit } from '@/hooks/useSubmit';
import { FoodPicker } from './FoodPicker';
import { Modal } from './Modal';
import { ErrorBanner } from './ui';

type Kind = 'weight' | 'meal' | 'water' | 'exercise';

const BUTTONS: { kind: Kind; label: string }[] = [
  { kind: 'weight', label: '⚖️ Poids' },
  { kind: 'meal', label: '🔥 Calories' },
  { kind: 'water', label: '💧 Eau' },
  { kind: 'exercise', label: '🏋️ Sport' },
];

const TITLES: Record<Kind, string> = {
  weight: 'Ajouter une pesée',
  meal: 'Ajouter des calories',
  water: 'Ajouter de l’eau',
  exercise: 'Ajouter une séance',
};

export function QuickAddBar() {
  const [open, setOpen] = useState<Kind | null>(null);
  const close = () => setOpen(null);
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h3 style={{ marginBottom: '0.6rem' }}>Ajout rapide</h3>
      <div className="toolbar" style={{ margin: 0, flexWrap: 'wrap' }}>
        {BUTTONS.map((b) => (
          <button type="button" key={b.kind} onClick={() => setOpen(b.kind)}>
            {b.label}
          </button>
        ))}
      </div>
      {open && (
        <Modal title={TITLES[open]} onClose={close}>
          {open === 'weight' && <WeightForm onDone={close} />}
          {open === 'meal' && <MealForm onDone={close} />}
          {open === 'water' && <WaterForm onDone={close} />}
          {open === 'exercise' && <ExerciseForm onDone={close} />}
        </Modal>
      )}
    </div>
  );
}

function WeightForm({ onDone }: { onDone: () => void }) {
  const create = useCreateWeight();
  const submit = useSubmit();
  const [date, setDate] = useState(todayKey());
  const [kg, setKg] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = Number(kg);
        if (!kg || !Number.isFinite(v)) return;
        void submit.run(async () => {
          await create.mutateAsync({ date, weight_kg: v });
          onDone();
        });
      }}
    >
      <div className="row">
        <div className="field">
          <label htmlFor="qa-wdate">Date</label>
          <input id="qa-wdate" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="qa-wkg">Poids (kg)</label>
          <input
            id="qa-wkg"
            type="number"
            step="0.1"
            min={20}
            max={400}
            value={kg}
            onChange={(e) => setKg(e.target.value)}
            placeholder="ex. 79.4"
            required
          />
        </div>
      </div>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}
      <button type="submit" disabled={submit.pending}>
        Ajouter
      </button>
    </form>
  );
}

function MealForm({ onDone }: { onDone: () => void }) {
  const { data: consumptions = [] } = useConsumptions();
  const create = useCreateConsumption();
  const submit = useSubmit();
  const [foodId, setFoodId] = useState('');
  const [grams, setGrams] = useState('');
  const [meal, setMeal] = useState<Meal>('lunch');

  const favorites = frequentFoods(
    consumptions.map((c) => ({ foodId: c.foodId, foodName: c.foodName, grams: c.grams })),
    4,
  );

  function logNow(fId: string, g: number, m: Meal) {
    void submit.run(async () => {
      await create.mutateAsync({
        food: fId,
        grams: g,
        datetime: new Date().toISOString(),
        meal: m,
      });
      onDone();
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const g = Number(grams);
        if (!foodId || !grams || !Number.isFinite(g) || g <= 0) return;
        logNow(foodId, g, meal);
      }}
    >
      {favorites.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            Favoris (portion médiane, ajout direct) :
          </span>
          <div className="toolbar" style={{ margin: '0.4rem 0 0', flexWrap: 'wrap' }}>
            {favorites.map((f) => (
              <button
                type="button"
                key={f.foodId}
                disabled={submit.pending}
                onClick={() => logNow(f.foodId, f.medianGrams, meal)}
              >
                {f.foodName} · {f.medianGrams} g
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="field">
        <label htmlFor="qa-food">Aliment</label>
        <FoodPicker id="qa-food" value={foodId} onChange={setFoodId} required />
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="qa-grams">Quantité (g)</label>
          <input
            id="qa-grams"
            type="number"
            min={0}
            max={10000}
            value={grams}
            onChange={(e) => setGrams(e.target.value)}
            placeholder="150"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="qa-meal">Repas</label>
          <select id="qa-meal" value={meal} onChange={(e) => setMeal(e.target.value as Meal)}>
            {MEAL_ORDER.map((m) => (
              <option key={m} value={m}>
                {MEAL_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}
      <button type="submit" disabled={submit.pending}>
        Ajouter
      </button>
    </form>
  );
}

const WATER_QUICK = [250, 500];

function WaterForm({ onDone }: { onDone: () => void }) {
  const create = useCreateWater();
  const submit = useSubmit();
  const [custom, setCustom] = useState('');

  function add(ml: number) {
    if (!Number.isFinite(ml) || ml <= 0) return;
    void submit.run(async () => {
      await create.mutateAsync({ ml, datetime: new Date().toISOString() });
      onDone();
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        add(Number(custom));
      }}
    >
      <div className="toolbar" style={{ margin: '0 0 0.75rem' }}>
        {WATER_QUICK.map((ml) => (
          <button type="button" key={ml} disabled={submit.pending} onClick={() => add(ml)}>
            + {ml} ml
          </button>
        ))}
      </div>
      <div className="field">
        <label htmlFor="qa-water">Quantité libre (ml)</label>
        <input
          id="qa-water"
          type="number"
          min={0}
          max={5000}
          step={50}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="ml"
        />
      </div>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}
      <button type="submit" className="ghost" disabled={submit.pending}>
        Ajouter
      </button>
    </form>
  );
}

function ExerciseForm({ onDone }: { onDone: () => void }) {
  const create = useCreateExercise();
  const submit = useSubmit();
  const [activity, setActivity] = useState<Activity>('running');
  const [kcal, setKcal] = useState('');
  const [duration, setDuration] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const burned = Number(kcal);
        if (!kcal || !Number.isFinite(burned) || burned < 0) return;
        void submit.run(async () => {
          await create.mutateAsync({
            activity,
            kcal_burned: burned,
            duration_min: Number(duration) || 0,
            note: '',
            datetime: new Date().toISOString(),
          });
          onDone();
        });
      }}
    >
      <div className="field">
        <label htmlFor="qa-act">Activité</label>
        <select
          id="qa-act"
          value={activity}
          onChange={(e) => setActivity(e.target.value as Activity)}
        >
          {SPORT_ORDER.map((a) => (
            <option key={a} value={a}>
              {SPORT_LABELS[a]}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="qa-kcal">Calories (kcal)</label>
          <input
            id="qa-kcal"
            type="number"
            min={0}
            max={10000}
            value={kcal}
            onChange={(e) => setKcal(e.target.value)}
            placeholder="ex. 350"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="qa-dur">Durée (min)</label>
          <input
            id="qa-dur"
            type="number"
            min={0}
            max={1440}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="30"
          />
        </div>
      </div>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}
      <button type="submit" disabled={submit.pending}>
        Ajouter
      </button>
    </form>
  );
}
